import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { EmptyRow, LoadingOverlay, OtpModal, RECEIVABLES_API, fmtDate, fmtDateTime, money, sumBy, todayIso } from "./common";

const CHEQUES_API = `${RECEIVABLES_API}/cheques`;
const keyOf = (row) => `${row.chequeNumber}_${row.customerId}`;
const chequeKey = (row) => ({ chequeNumber: row.chequeNumber, customerId: row.customerId });

const SectionTitle = ({ children, actions }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "22px 0 0", gap: 10, flexWrap: "wrap" }}>
    <h3 style={{ fontWeight: 600, margin: 0 }}>{children}</h3>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>
  </div>
);

/** Cheque lifecycle: clear / approve, bounce, bounce & reverse, payment received, pay-in slip (legacy cheque_report.aspx). */
const ChequeReport = () => {
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [dateBasis, setDateBasis] = useState("CHEQUE_DATE");
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [pendingOtp, setPendingOtp] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => ({ fromDate, toDate, dateBasis });

  const load = async () => {
    if (fromDate > toDate) {
      toast.error("From date cannot be after to date");
      return;
    }
    setLoading(true);
    try {
      setReport(await api.get(CHEQUES_API, params()));
      setSelected(new Set());
    } catch (error) {
      toast.error(error.message || "Failed to load cheques");
    } finally {
      setLoading(false);
    }
  };

  const act = async (work, { reload = true } = {}) => {
    setLoading(true);
    try {
      const result = await work();
      if (result?.otpRequired) {
        setPendingOtp(result);
      } else if (result?.message) {
        toast.success(result.message);
      }
      if (reload && !result?.otpRequired) await load();
    } catch (error) {
      toast.error(error.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const download = (path, extraParams, filename) =>
    act(() => api.downloadFile(path, extraParams, filename), { reload: false });

  const pending = report?.pending || [];
  const selectedRows = pending.filter((row) => selected.has(keyOf(row)));
  const requireSelection = () => {
    if (selectedRows.length === 0) throw new Error("Select at least one cheque");
    return { cheques: selectedRows.map(chequeKey) };
  };

  const clearOrApprove = () =>
    act(() => api.post(`${CHEQUES_API}/${report.approvalForCheque ? "approve" : "clear"}`, requireSelection()));

  const bounce = (row) => {
    if (!window.confirm(`Mark cheque ${row.chequeNumber} of ${row.customerName} (₹${money(row.amount)}) as bounced?`)) return;
    act(() => api.post(`${CHEQUES_API}/bounce`, chequeKey(row)));
  };

  const bounceAndReverse = (row) => {
    if (!window.confirm(`Bounce approved cheque ${row.chequeNumber} and reverse ₹${money(row.amount)} from its invoices?`)) return;
    act(() => api.post(`${CHEQUES_API}/bounce-and-reverse`, chequeKey(row)));
  };

  const paymentReceived = (row) => {
    if (!window.confirm(`Record that payment was received for bounced cheque ${row.chequeNumber}?`)) return;
    act(() => api.post(`${CHEQUES_API}/payment-received`, chequeKey(row)));
  };

  const realise = () =>
    act(() => {
      if (selectedRows.length === 0) throw new Error("Select at least one cheque");
      return api.post(`${CHEQUES_API}/realise`, { ids: selectedRows.flatMap((row) => row.collectionIds) });
    });

  /** Cheque deposit (pay-in) slip of the selected cheques: "xlsx" or "pdf". */
  const payInSlip = (format) =>
    act(
      () => {
        if (selectedRows.length === 0) throw new Error("Select at least one cheque");
        const path = format === "pdf" ? `${CHEQUES_API}/pay-in-slip/pdf` : `${CHEQUES_API}/pay-in-slip`;
        return api.downloadFile(path, { keys: selectedRows.map(keyOf).join(",") }, `pay-in-slip-${todayIso()}.${format}`);
      },
      { reload: false }
    );

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allSelected = pending.length > 0 && pending.every((row) => selected.has(keyOf(row)));

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="filters-bar">
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <input type="radio" checked={dateBasis === "CHEQUE_DATE"} onChange={() => setDateBasis("CHEQUE_DATE")} />
          Cheque date
        </label>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <input type="radio" checked={dateBasis === "COLLECTION_DATE"} onChange={() => setDateBasis("COLLECTION_DATE")} />
          Collection date
        </label>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          Generate
        </button>
      </div>

      {report ? (
        <>
          <SectionTitle
            actions={[
              <button key="clear" className="primary" onClick={clearOrApprove} disabled={selectedRows.length === 0}>
                {report.approvalForCheque ? "Approve Selected" : "Clear Selected"}
              </button>,
              <button key="realise" className="secondary" onClick={realise} disabled={selectedRows.length === 0}>
                Mark Realised
              </button>,
              <button key="slip" className="secondary" onClick={() => payInSlip("xlsx")} disabled={selectedRows.length === 0}
                title="Cheque deposit slip of the selected cheques">
                Pay-in Slip (Excel)
              </button>,
              <button key="slip-pdf" className="secondary" onClick={() => payInSlip("pdf")} disabled={selectedRows.length === 0}
                title="Cheque deposit slip of the selected cheques">
                Pay-in Slip (PDF)
              </button>,
              <button key="export" className="secondary" onClick={() => download(`${CHEQUES_API}/export`, params(), "cheque-report.xlsx")}>
                Export
              </button>,
            ]}
          >
            Pending cheques ({pending.length})
          </SectionTitle>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(pending.map(keyOf)))}
                      aria-label="Select all cheques"
                    />
                  </th>
                  <th>Customer</th>
                  <th>Invoice(s)</th>
                  <th>Beat</th>
                  <th>Salesman</th>
                  <th>Cheque No</th>
                  <th>Collected</th>
                  <th>Cheque Date</th>
                  <th>Bank</th>
                  <th>Branch</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Realised</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.length === 0 ? (
                  <EmptyRow colSpan={13} title="No pending cheques in this range" />
                ) : (
                  pending.map((row) => (
                    <tr key={keyOf(row)}>
                      <td>
                        <input type="checkbox" checked={selected.has(keyOf(row))} onChange={() => toggle(keyOf(row))} />
                      </td>
                      <td title={row.customerName}>{row.customerName}</td>
                      <td title={row.invoiceNumbers}>{row.invoiceNumbers}</td>
                      <td>{row.beatName || "-"}</td>
                      <td>{row.salesmanName || "-"}</td>
                      <td>{row.chequeNumber}</td>
                      <td>{fmtDateTime(row.collectedAt)}</td>
                      <td>{fmtDate(row.chequeDate)}</td>
                      <td>{row.bankName || "-"}</td>
                      <td>{row.branchName || "-"}</td>
                      <td style={{ textAlign: "right" }}>{money(row.amount)}</td>
                      <td>{row.realised ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span>}</td>
                      <td>
                        <button className="secondary" onClick={() => bounce(row)}>
                          Bounce
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {pending.length ? (
                <tfoot>
                  <tr>
                    <td colSpan={10} style={{ fontWeight: 700 }}>
                      No of cheques: {pending.length} {selectedRows.length ? `(selected ${selectedRows.length}: ₹${money(sumBy(selectedRows, (r) => r.amount))})` : ""}
                    </td>
                    <td style={{ fontWeight: 700, textAlign: "right" }}>{money(sumBy(pending, (r) => r.amount))}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <SectionTitle
            actions={[
              <button key="export" className="secondary" onClick={() => download(`${CHEQUES_API}/bounced/export`, {}, "bounced-cheques.xlsx")}>
                Export
              </button>,
            ]}
          >
            Bounced cheques awaiting payment ({report.bounced.length})
          </SectionTitle>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Invoice(s)</th>
                  <th>Beat</th>
                  <th>Salesman</th>
                  <th>Cheque No</th>
                  <th>Cheque Date</th>
                  <th>Bounced On</th>
                  <th>Bank</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "right" }}>Charges</th>
                  <th style={{ textAlign: "right" }}>Current Balance</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {report.bounced.length === 0 ? (
                  <EmptyRow colSpan={12} title="No open bounced cheques" />
                ) : (
                  report.bounced.map((row) => (
                    <tr key={row.bounceId} style={{ background: "#fef2f2" }}>
                      <td title={row.customerName}>{row.customerName}</td>
                      <td title={row.invoiceNumbers}>{row.invoiceNumbers || "-"}</td>
                      <td>{row.beatName || "-"}</td>
                      <td>{row.salesmanName || "-"}</td>
                      <td>{row.chequeNumber}</td>
                      <td>{fmtDate(row.chequeDate)}</td>
                      <td>{fmtDateTime(row.bouncedAt)}</td>
                      <td>{row.bankName || "-"}</td>
                      <td style={{ textAlign: "right" }}>{money(row.amount)}</td>
                      <td style={{ textAlign: "right" }}>{money(row.bounceCharges)}</td>
                      <td style={{ textAlign: "right" }}>{money(row.currentBalance)}</td>
                      <td>
                        <button className="secondary" onClick={() => paymentReceived(row)}>
                          Payment Received
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <SectionTitle
            actions={[
              <button
                key="export"
                className="secondary"
                onClick={() => download(`${CHEQUES_API}/approved/export`, params(), "approved-cheques.xlsx")}
              >
                Export
              </button>,
            ]}
          >
            Approved / cleared cheques ({report.approved.length})
          </SectionTitle>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Invoice(s)</th>
                  <th>Beat</th>
                  <th>Salesman</th>
                  <th>Cheque No</th>
                  <th>Cheque Date</th>
                  <th>Approved On</th>
                  <th>Bank</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  {report.bounceAndReverseEnabled ? <th>Action</th> : null}
                </tr>
              </thead>
              <tbody>
                {report.approved.length === 0 ? (
                  <EmptyRow colSpan={report.bounceAndReverseEnabled ? 10 : 9} title="No approved cheques in this range" />
                ) : (
                  report.approved.map((row) => (
                    <tr key={keyOf(row)}>
                      <td title={row.customerName}>{row.customerName}</td>
                      <td title={row.invoiceNumbers}>{row.invoiceNumbers}</td>
                      <td>{row.beatName || "-"}</td>
                      <td>{row.salesmanName || "-"}</td>
                      <td>{row.chequeNumber}</td>
                      <td>{fmtDate(row.chequeDate)}</td>
                      <td>{fmtDateTime(row.approvedAt)}</td>
                      <td>{row.bankName || "-"}</td>
                      <td style={{ textAlign: "right" }}>{money(row.amount)}</td>
                      {report.bounceAndReverseEnabled ? (
                        <td>
                          <button className="secondary" onClick={() => bounceAndReverse(row)} disabled={!row.approved}>
                            Bounce & Reverse
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="page-subtitle" style={{ marginTop: 16 }}>
          Choose a date range and press Generate.
        </div>
      )}

      <OtpModal
        pending={pendingOtp}
        verifyPath={(id) => `${CHEQUES_API}/approval-requests/${id}/verify`}
        onClose={() => setPendingOtp(null)}
        onVerified={() => {
          setPendingOtp(null);
          load();
        }}
      />
    </div>
  );
};

export default ChequeReport;
